import clsx from 'clsx';
import React, { useMemo } from 'react';
import { match } from 'ts-pattern';

import { getTextSize, type TextSizeType } from '../utils';

export type Props = Omit<JSX.IntrinsicElements['input'], 'ref' | 'size'> & {
  border?: boolean;
  size?: TextSizeType | 'inherit';
  flexContainer?: string; // It is nearly impossible to make a generic forwardRef component
  hasError?: boolean;
  label?: React.ReactNode;
  width?: string;
  padding?: string;
  inverted?: boolean;
  background?: string;
  copyable?: boolean;
  align?: 'left' | 'right' | 'center';
  'data-testid'?: string;
  transparent?: boolean;
  extraClassNames?: string;
  autoComplete?: boolean;
  labelClassName?: string;
};

export const TextInput = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      size = 'sm',
      border = false,
      hasError = false,
      align = 'left',
      id,
      label,
      className,
      width,
      padding,
      background,
      inverted = false,
      'data-testid': dataTestId,
      flexContainer,
      transparent = false,
      color = 'text-main',
      extraClassNames,
      autoComplete = false,
      labelClassName,
      ...props
    },
    ref
  ) => {
    const colorClasses = useMemo(() => {
      if (inverted) {
        if (props.disabled)
          return 'bg-main text-main-500  hover:cursor-not-allowed';
        return 'bg-main text-negative';
      }
      if (transparent) {
        if (props.disabled)
          return 'bg-transparent text-main-500 hover:cursor-not-allowed';
        return [
          'bg-transparent',
          'hover:bg-transparent',
          'group-hover/text-input:bg-transparent',
          'focus:bg-transparent',
        ];
      }
      if (props.disabled) {
        return [
          'text-main-500',
          'bg-main-200',
          'group-hover/text-input:cursor-not-allowed',
          'focus:bg-main-200',
          'group-hover/text-input:bg-main-200',
        ];
      }
      return [
        'bg-main-200',
        'hover:bg-negative',
        'group-hover/text-input:bg-negative',
        'focus:bg-negative',
      ];
    }, [inverted, transparent, props.disabled]);

    const borderClasses = useMemo(() => {
      if (hasError) {
        return ['border border-orange', 'focus:border-orange'];
      }
      if (border) {
        return [
          'border box-border',
          'focus:border-main-300',
          'border-main-300',
          'focus:ring-transparent',
          'active:ring-transparent',
          'active:outline-none',
        ];
      }
      return ['border-none', 'focus:border-none'];
    }, [border, hasError]);
    const textSizeClass =
      size === 'inherit'
        ? 'text-[length:inherit] leading-[inherit]'
        : getTextSize(size);
    return (
      <div
        className={clsx(
          'w-full',
          flexContainer ?? 'flex',
          'group/text-input items-center'
        )}
      >
        {label &&
          match(typeof label)
            .with('string', () => (
              <label htmlFor={id} className="block w-fit">
                <span
                  className={clsx(
                    textSizeClass,
                    'text-main-500',
                    labelClassName
                  )}
                >
                  {label}
                </span>
              </label>
            ))
            .otherwise(() => (
              <div
                className={clsx(
                  'flex items-center',
                  props.height ?? 'h-f2',
                  !props.disabled && 'group-hover/text-input:bg-negative',
                  !props.disabled &&
                    'group-focus-within/text-input:bg-negative',
                  textSizeClass
                )}
              >
                {label}
              </div>
            ))}
        <div className={clsx(width)}>
          <input
            data-testid={dataTestId}
            type="text"
            ref={ref}
            id={id}
            autoComplete={autoComplete ? 'on' : 'off'}
            data-form-type={autoComplete ? 'text' : 'other'} // prevents dashlane from autocompleting
            className={clsx(
              width,
              colorClasses,
              borderClasses,
              ['outline-none', 'focus:outline-none', 'active:outline-none'],
              ['focus:ring-transparent', 'active:ring-transparent'],
              textSizeClass,
              [color, `placeholder:text-main-500`],
              padding,
              'min-w-0',
              // eslint-disable-next-line no-nested-ternary
              align === 'left'
                ? 'text-left'
                : align === 'center'
                ? 'text-center'
                : 'text-right',
              className,
              background,
              extraClassNames
            )}
            {...props}
          />
        </div>
      </div>
    );
  }
);
